/* tia-pdp-geometry.js — the ONE panel-geometry engine for the Tiaracle PDP.
 *
 * Created 2026-08-16 for the classic-plus canary template. The table, the panel solver,
 * the size key, the live-layout builder, the selection reader and the layout matcher are
 * all LIFTED VERBATIM from snippets/tia-size-chart.liquid, so the true-to-scale preview
 * and the size chart cannot disagree about which layout is selected or where a panel sits.
 *
 * Absolute inches, including the installed footprint (iw/ih) where it differs from the
 * listed size — NOT the fractional GEO table the tia-pdp-new prototype carries. Inches are
 * what a drawing against real furniture needs, and the prototype's table covers 6 layouts
 * against this one's 14.
 *
 * 14 entries: 1 Piece horizontal AND panoramic, 3/4/5 Horizontal, 4 Pop (diagonal cascade
 * via `tops`, installed footprint taller than the listed height), 5 Pop, 5 Star pyramid,
 * 4 Square grid, 1 and 3 Vertical, 3 Piece panoramic, Framed Canvas, Framed Print.
 *
 * Layouts are matched by OPTION VALUE + material + the size SET the product actually
 * sells, never by position: the catalogue carries several option orderings, and `1 Piece`
 * is shared between the horizontal range (24x16 to 48x32) and the panoramic one (36x12 to
 * 60x20). Option NAMES are matched tolerantly on both generations of the rename
 * (pieces|layout|panel, frame|material|finish), so this survives the Pieces->Layout and
 * Frame Option->Finish migration in flight.
 *
 * 🔴 THE TABLE CARRIES EVERY SIZE A LAYOUT IS SOLD IN, NOT ONE PRODUCT'S LADDER. buildLive()
 * intersects each row with the product's own variants, so a row that is missing a size
 * silently drops that size from the chips and draws the nearest one as "true to scale".
 * That is exactly what happened on 2026-09-12: 1 Vertical carried the pre-2026-08-24
 * three-rung ladder (12x24 / 18x36 / 24x48) after the vertical range had been migrated to
 * five rungs (12x24 / 16x32 / 20x40 / 24x48 / 30x60; a 201-product minority keeps the old
 * three, so 18x36 stays), so a 16x32 selection showed "Small 12x24 / Large 24x48", no
 * YOURS, and a 12x24 drawn on the wall. 4 Horizontal listed 35x24 while every product sells
 * 34" x 24" (installed 33.5), so its small rung never matched anything.
 *   Sizes are the store's listed strings; iw/ih is the installed footprint from the
 *   printed hanging guides (seo-content/size-chart-rebuild-2026-08-30/data.py), and the
 *   panel gap on every multi-panel layout is 0.5in, so pw x n + 0.5 x (n-1) = iw.
 *
 * 🔴 SIZE TIERS ARE DERIVED, NOT STORED. "Small / Medium / Large" are the names of a
 * three-rung ladder and mean nothing on a five-rung one, so buildLive() labels the LIVE
 * sizes after the intersection: exactly three get Small / Medium / Large, any other count
 * gets no tier and the chip shows the size itself. Same rule as the custom studio
 * (sections/tia-customizer.liquid, paintWall), so the two tools cannot teach differently.
 *
 * 🔴 DEPTH IS ONE NUMBER. The store's canvas is 1.25in (Dreamship item 19, "Standard Matte
 * Canvas (1.25\")", FULFILMENT-RULES 1d), which the custom studio and the render gallery
 * already state. The old per-tier table (0.7 / 0.7 / 1.1) understated every size and was
 * flagged in CHANGELOG 2026-09-02a; the owner asked on 2026-08-24 for 1.25in to be stated
 * on the vertical product page. DEPTH_IN replaces it. A Framed Print gets no depth figure
 * (paper behind glass, frame depth unknown here), and its consumers already skip it.
 *
 * 🔴 KNOWN DUPLICATION, DELIBERATE AND TIME-BOXED: tia-size-chart.liquid still carries its
 * own inline copy of this code. It was left untouched on purpose — it renders the size
 * chart for every product on the classic page, and rewiring it to consume this asset is a
 * change to live-serving code that should not ride on an unapproved experiment. If the
 * classic-plus direction is adopted, the consolidation step is to delete the inline copy
 * and have the size chart read window.TiaPdpGeo. Until then: EDIT BOTH OR NEITHER.
 */
(function (w) {
  var DEPTH_IN=1.25;   /* the store's canvas wrap, one product fact for every canvas size */
  var L=[
  {n:'1 Panel',opt:'1 Piece',mat:'Canvas Print',arr:'row',s:[
   {w:24,h:16,pw:24,hs:[16]},{w:36,h:24,pw:36,hs:[24]},{w:48,h:32,pw:48,hs:[32]}]},
  {n:'3 Horizontal',opt:'3 Horizontal',mat:'Canvas Print',arr:'row',s:[
   {w:25,h:16,pw:8,hs:[16,16,16]},{w:37,h:24,pw:12,hs:[24,24,24]},{w:49,h:32,pw:16,hs:[32,32,32]}]},
  /* 4 Horizontal is sold as 34x24 / 43x30 / 50x36; four panels on a 0.5in gap install at
     33.5 / 41.5 / 49.5 wide (guide numbers, data.py 4horiz). */
  {n:'4 Horizontal',opt:'4 Horizontal',mat:'Canvas Print',arr:'row',s:[
   {w:34,h:24,iw:33.5,pw:8,hs:[24,24,24,24]},{w:43,h:30,iw:41.5,pw:10,hs:[30,30,30,30]},{w:50,h:36,iw:49.5,pw:12,hs:[36,36,36,36]}]},
  {n:'5 Horizontal',opt:'5 Horizontal',mat:'Canvas Print',arr:'row',s:[
   {w:22,h:12,pw:4,hs:[12,12,12,12,12]},{w:42,h:24,pw:8,hs:[24,24,24,24,24]},{w:52,h:30,pw:10,hs:[30,30,30,30,30]}]},
  /* 4 Pop from the Tiaracle 4-Pop hanging guide: a diagonal cascade, not a symmetric pair.
     Panel 2 hangs at the top reference, panel 3 at half the outer offset, 1 and 4 at the full
     offset. The catalog size is ROUNDED - the installed footprint (iw x ih) is what goes on
     the wall and is taller than the listed height by 1.5 to 3 in. */
  {n:'4 Pop',opt:'4 Pop',mat:'Canvas Print',arr:'row',s:[
   {w:34,h:20,iw:33.5,ih:21.5,pw:8,hs:[16,20,20,16],tops:[3,0,1.5,3]},
   {w:50,h:32,iw:49.5,ih:34.5,pw:12,hs:[24,32,32,24],tops:[5,0,2.5,5]},
   {w:62,h:40,iw:61.5,ih:43,pw:15,hs:[30,40,40,30],tops:[6,0,3,6]}]},
  {n:'5 Pop',opt:'5 Pop',mat:'Canvas Print',arr:'row',s:[
   {w:32,h:21,pw:6,hs:[16,16,21,16,16],tops:[2.5,4,0,2.5,4]},
   {w:47,h:32,pw:9,hs:[24,24,32,24,24],tops:[4,6,0,4,6]},
   {w:62,h:42,pw:12,hs:[32,32,42,32,32],tops:[5,8,0,5,8]}]},
  {n:'5 Star',opt:'5 Star',mat:'Canvas Print',arr:'row',s:[
   {w:42,h:21,pw:8,hs:[10,16,21,16,10]},{w:62,h:32,pw:12,hs:[16,24,32,24,16]},{w:82,h:42,pw:16,hs:[21,32,42,32,21]}]},
  {n:'4 Square',opt:'4 Square',mat:'Canvas Print',arr:'grid',s:[
   {w:17,h:17,pw:8,ph:8},{w:37,h:37,pw:18,ph:18},{w:63,h:63,pw:31,ph:31}]},
  {n:'1 Panel',opt:'1 Piece',mat:'Canvas Print',arr:'row',s:[
   {w:36,h:12,pw:36,hs:[12]},{w:48,h:16,pw:48,hs:[16]},{w:60,h:20,pw:60,hs:[20]}]},
  {n:'3 Piece',opt:'3 Piece',mat:'Canvas Print',arr:'row',s:[
   {w:25,h:8,pw:8,hs:[8,8,8]},{w:56,h:18,iw:55,pw:18,hs:[18,18,18]},{w:73,h:24,pw:24,hs:[24,24,24]}]},
  /* 1 Vertical: the five-rung ladder most vertical artwork sells since 2026-08-24, plus the
     18x36 that the three-rung minority (12x24 / 18x36 / 24x48) still carries. No product
     sells all six; the intersection with its variants decides. */
  {n:'1 Vertical',opt:'1 Vertical',mat:'Canvas Print',arr:'row',s:[
   {w:12,h:24,pw:12,hs:[24]},{w:16,h:32,pw:16,hs:[32]},{w:18,h:36,pw:18,hs:[36]},
   {w:20,h:40,pw:20,hs:[40]},{w:24,h:48,pw:24,hs:[48]},{w:30,h:60,pw:30,hs:[60]}]},
  {n:'3 Vertical',opt:'3 Vertical',mat:'Canvas Print',arr:'stack',s:[
   {w:12,h:25,pw:12,ph:8,cnt:3},{w:18,h:37,pw:18,ph:12,cnt:3},{w:24,h:49,pw:24,ph:16,cnt:3}]},
  {n:'1 Panel',opt:'1 Piece',mat:'Framed Canvas',arr:'row',s:[
   {w:24,h:16,pw:24,hs:[16]},{w:36,h:24,pw:36,hs:[24]},{w:48,h:32,pw:48,hs:[32]}]},
  {n:'1 Panel',opt:'1 Piece',mat:'Framed Print',arr:'row',s:[
   {w:24,h:16,pw:24,hs:[16]},{w:30,h:20,pw:30,hs:[20]},{w:36,h:24,pw:36,hs:[24]}]}
  ];
  var SOFA_W=84,SOFA_H=34,CLEAR=10;
  var TIER=[['var(--tsc-pA)','var(--tsc-pAs)'],['var(--tsc-pB)','var(--tsc-pBs)'],['var(--tsc-pC)','var(--tsc-pCs)']];
  var f=function(n){return Math.round(n*100)/100;};
  var inch=function(n){return (n%1===0?n:f(n))+'″';};
  var dim=function(w,h){return inch(w)+' × '+inch(h);};
  var key=function(s){var m=String(s).match(/(\d+(?:\.\d+)?)[^0-9]+(\d+(?:\.\d+)?)/);
    return m?(parseFloat(m[1])+'x'+parseFloat(m[2])):String(s).toLowerCase();};

  function DW(s){return s.iw||s.w;} function DH(s){return s.ih||s.h;}
  function panels(l,s){
    var P=[],i,gap,W=DW(s),H=DH(s);
    if(l.arr==='row'){
      var n=s.hs.length; gap=n>1?(W-n*s.pw)/(n-1):0;
      for(i=0;i<n;i++) P.push({x:i*(s.pw+gap),y:s.tops?s.tops[i]:(H-s.hs[i])/2,w:s.pw,h:s.hs[i]});
    }else if(l.arr==='stack'){
      gap=(H-s.cnt*s.ph)/(s.cnt-1);
      for(i=0;i<s.cnt;i++) P.push({x:0,y:i*(s.ph+gap),w:s.pw,h:s.ph});
    }else{
      var gx=W-2*s.pw, gy=H-2*s.ph;
      [[0,0],[s.pw+gx,0],[0,s.ph+gy],[s.pw+gx,s.ph+gy]].forEach(function(c){P.push({x:c[0],y:c[1],w:s.pw,h:s.ph});});
    }
    var hs=P.map(function(p){return p.h;}).filter(function(v,i2,a){return a.indexOf(v)===i2;}).sort(function(a,b){return b-a;});
    P.forEach(function(p){p.tier = hs.length===1 ? 1 : Math.min(hs.indexOf(p.h),2);});
    return P;
  }

  /* ---- resolution pipeline, lifted from tia-size-chart.liquid boot() ---- */

  /* Which option index is layout / material / size, matched by NAME and tolerant of both
     option-name generations (Pieces->Layout, Frame Option->Finish). Returns -1 for any
     axis the product does not carry — Custom Prints have no layout option at all. */
  function axes(names) {
    var n = (names || []).map(function (x) { return String(x).toLowerCase(); });
    var iPie = -1, iMat = -1, iSz = -1;
    n.forEach(function (nm, i) {
      if (nm.indexOf('size') > -1 || nm.indexOf('dimension') > -1) iSz = i;
      else if (nm.indexOf('frame') > -1 || nm.indexOf('material') > -1 || nm.indexOf('finish') > -1) iMat = i;
      else if (nm.indexOf('piece') > -1 || nm.indexOf('layout') > -1 || nm.indexOf('panel') > -1) iPie = i;
    });
    return { pie: iPie, mat: iMat, sz: iSz };
  }

  /* Copies of the live sizes, in table order, with the tier name derived from the count:
     a three-rung ladder is Small / Medium / Large, anything else has no tier name and the
     chip shows the size itself. Copies, so `yours` and the tier never touch the shared table. */
  var TIERS = ['Small', 'Medium', 'Large'];
  function tiered(sizes) {
    return sizes.map(function (s, i) {
      var o = {}, k2;
      for (k2 in s) if (Object.prototype.hasOwnProperty.call(s, k2)) o[k2] = s[k2];
      o.t = sizes.length === 3 ? TIERS[i] : '';
      return o;
    });
  }

  /* The layouts this product really sells, each trimmed to the sizes it really offers.
     Empty result means the drawing cannot be trusted and the caller must hide itself. */
  function buildLive(names, variants) {
    var ax = axes(names), have = {}, live = [];
    (variants || []).forEach(function (v) {
      have[(ax.pie > -1 ? v[ax.pie] : '1 Piece') + '|' +
           (ax.mat > -1 ? v[ax.mat] : 'Canvas Print') + '|' +
           key(ax.sz > -1 ? v[ax.sz] : '')] = 1;
    });
    if (ax.pie > -1) {
      L.forEach(function (l) {
        var sizes = l.s.filter(function (s) { return have[l.opt + '|' + l.mat + '|' + key(s.w + 'x' + s.h)]; });
        if (sizes.length) live.push({ l: l, sizes: tiered(sizes) });
      });
    } else {
      /* No layout option (Custom Prints). The size SET is unique to one layout. */
      var ps = {}, pn = 0;
      (variants || []).forEach(function (v) {
        var k2 = key(ax.sz > -1 ? v[ax.sz] : ''); if (k2 && !ps[k2]) { ps[k2] = 1; pn++; }
      });
      L.forEach(function (l) {
        if (live.length) return;
        var ls = l.s.map(function (x) { return key(x.w + 'x' + x.h); });
        if (ls.length === pn && ls.every(function (k2) { return ps[k2]; })) live.push({ l: l, sizes: tiered(l.s) });
      });
    }
    return { axes: ax, live: live };
  }

  /* Read the buy box. Primary contract is the tia-buy-box radios; the legacy .swatch form
     is the fallback for any context that still renders it. */
  function readSelection(ax) {
    var vals = [], groups = document.querySelectorAll('[data-tia-buy] [data-tia-opt]');
    if (groups.length) {
      Array.prototype.forEach.call(groups, function (g) {
        var i = parseInt(g.getAttribute('data-tia-opt'), 10);
        var c = g.querySelector('input[type="radio"]:checked'); if (c) vals[i] = c.value;
      });
    } else {
      var form = document.querySelector('.product_section .product_form');
      Array.prototype.forEach.call((form || document).querySelectorAll('.swatch[data-option-index]'), function (g) {
        var i = parseInt(g.getAttribute('data-option-index'), 10);
        var c = g.querySelector('input[type="radio"]:checked'), sel = g.querySelector('select');
        var v = c ? c.value : (sel ? sel.value : null); if (v) vals[i] = v;
      });
    }
    return {
      pie: ax.pie > -1 ? vals[ax.pie] : null,
      mat: ax.mat > -1 ? vals[ax.mat] : null,
      sz:  ax.sz  > -1 ? vals[ax.sz]  : null
    };
  }

  /* Pick the live entry and the size tier for a selection. The size fallback is what
     separates the panoramic 1 Piece from the horizontal one when both match on value. */
  function matchEntry(live, sel, prevIdx) {
    var match = live.filter(function (e) {
      return (!sel.pie || e.l.opt === sel.pie) && (!sel.mat || e.l.mat === sel.mat);
    })[0];
    if (sel.sz) {
      var byS = live.filter(function (e) {
        return (!sel.pie || e.l.opt === sel.pie) && (!sel.mat || e.l.mat === sel.mat) &&
          e.sizes.some(function (x) { return key(x.w + 'x' + x.h) === key(sel.sz); });
      })[0];
      if (byS) match = byS;
    }
    var entry = match || live[0];
    var yi = -1;
    entry.sizes.forEach(function (x, i) { if (sel.sz && key(x.w + 'x' + x.h) === key(sel.sz)) yi = i; });
    var idx = yi > -1 ? yi : Math.min(prevIdx == null ? 0 : prevIdx, entry.sizes.length - 1);
    return { entry: entry, sizeIdx: Math.max(0, idx), exact: yi > -1 };
  }

  w.TiaPdpGeo = {
    L: L, DEPTH_IN: DEPTH_IN, panels: panels, DW: DW, DH: DH, key: key,
    axes: axes, buildLive: buildLive, readSelection: readSelection, matchEntry: matchEntry
  };
})(window);
